<template>
    <main
        v-if="data"
        class="relative pt-25 md:pt-36">
        <TheBreadcrumbs :items="breadcrumbs" />

        <ProductPreview
            v-if="data?.data.model_range_info.image.length"
            :product="productPreviewItem"
            :direction-slug="directionSlug"
            :calculator="calculator"
            class="mt-5" />

        <ThesesSection
            v-if="data?.data.model_range_theses.data?.length"
            :items="data.data.model_range_theses.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <ProductLineBlocksSection
            v-if="data?.data.model_range_description.data.length"
            :data="data.data.model_range_description.data[0]"
            class="py-10 md:py-20" />
        <AtomHr />

        <ProductLineProductsSection
            v-if="data.data.products.data.length"
            :catalog-category="data?.data.category_slug"
            :items="data.data.products.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <ProductLineInterestingSection
            v-if="data?.data.be_interesting.data.length"
            :catalog-category="data.data.category_slug"
            :items="data.data.be_interesting.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <NewsSection
            v-if="data?.data.news.data.length"
            title="Читайте также:"
            :items="data.data.news.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <WarrantySection
            :direction-slug="directionSlug"
            class="py-10 md:py-20" />
        <AtomHr />

        <RemainingQuestionsSection class="py-10 md:py-20" />
        <AtomHr />

        <InviteSection class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import { useModalStore } from '#imports'
import { getCalculator } from '~/services/calculator.service'
import type { TDirectionSlug } from '~/types/global.types'
import type { ProductLinePage } from '~/types/pages.types'

import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import InviteSection from '~/components/pages/directions/invite-section/InviteSection.vue'
import NewsSection from '~/components/particles/news-section/NewsSection.vue'
import ProductLineBlocksSection from '~/components/particles/product-line-blocks-section/ProductLineBlocksSection.vue'
import ProductLineInterestingSection from '~/components/particles/product-line-interesting-section/ProductLineInterestingSection.vue'
import ProductLineProductsSection from '~/components/particles/product-line-products-section/ProductLineProductsSection.vue'
import ProductPreview from '~/components/particles/product-preview/ProductPreview.vue'
import type { ProductPreviewProps } from '~/components/particles/product-preview/product-preview.types'
import RemainingQuestionsSection from '~/components/particles/remaining-questions-section/RemainingQuestionsSection.vue'
import ThesesSection from '~/components/particles/theses-section/ThesesSection.vue'
import WarrantySection from '~/components/particles/warranty-section/WarrantySection.vue'

import { defineSeoAggregateRating } from '~/utils/seo'

const route = useRoute()
const nuxtApp = useNuxtApp()
const modalStore = useModalStore()
const directionSlug = computed(
    () => String(route.params.direction) as TDirectionSlug
)

const { data, error } = await useApiFetch<Page<ProductLinePage>>(
    `/v1/categories/${route.params.direction}/products/${route.params.subcategory}`,
    {
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        query: {
            include: [
                'model_range_theses',
                'calculator',
                'model_range_description',
                'model_range_description.blocks',
                'be_interesting',
                'products',
                'news',
            ].join(','),
        },
        useLastModified: true,
    }
)
if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
    modalStore.setNameProduct(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

const calculator = computed(() =>
    getCalculator(
        directionSlug.value,
        data.value?.data?.calculator?.data || null
    )
)

const productPreviewItem = computed<ProductPreviewProps | null>(
    (): ProductPreviewProps | null => {
        if (!data.value) return null

        return {
            feedback_label: data.value.data.feedback_label,
            modalButtonStretch: false,
            modalButtonText: 'Коммерческое предложение',
            focus: 'mobile',
            videoLink:
                data.value.data.model_range_info.link_rutube || undefined,
            videoPreview:
                data.value.data.model_range_info.rutube_preview?.[0] ||
                undefined,
            estimated_cost: '',
            images: data.value.data.model_range_info.image || [],
            title: data.value.data.model_range_name || data.value.data.name,
            price: data.value.data.model_range_info.price || undefined,
            isAvailable: data.value.data.model_range_info.is_available,
            description: data.value.data.description || undefined,
        }
    }
)

const breadcrumbs: BreadcrumbItem[] = [
    {
        label: 'Cromi',
        to: '/',
    },
    {
        label: data.value?.data.parent_name || '',
        to: '/' + data.value?.data.parent_slug,
    },
    {
        label: data.value?.data.name || '',
    },
]

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })
}

if (data.value) {
    useSchemaOrg([
        {
            '@type': 'Product',
            '@id': 'subcategory-product',
            name: data.value.data.name,
            description: stripHtml(data.value.data.description),
            url: route.path,
            image: (data.value.data.image || []).map((image) =>
                defineSeoImage({
                    ...image,
                    url: getMediaLink(image.url) || image.url,
                })
            ),
            sku: data?.value.data.id,
            brand: {
                '@type': 'Brand',
                name: data.value.data.brand ? data.value.data.brand : 'Cromi',
            },
            offers: defineOffer({
                price: data?.value.data.model_range_info.price,
                priceCurrency: 'RUB',
                availability: 'https://schema.org/InStock',
                // shippingDetails: defineShippingDetails(),
                // seller: defineOrganization(organization),
            }),
            aggregateRating: defineSeoAggregateRating(),
        },
    ])
}
</script>
